// CompareThermalView - Thermal section with table/2d/3d views
import { h } from 'preact'
import { useState, useEffect } from 'preact/hooks'
import { compareCells, hiddenCells } from '../../../state/filterStore.js'
import { activeThermalSubSection, domain } from '../../../state/globalSignals.js'
import { getCellNormalized } from '../../../modules/dataService.js'
import DimensionViewMultiCell, { COMPARE_COLORS } from '../plots/DimensionViewMultiCell.jsx'
import { COMPARE_THERMAL_SECTION } from '../../../data/definitions.js'

// SVG Icons
const HeatIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2v10"></path>
    <path d="M18.4 6.6a9 9 0 1 1-12.77.04"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
)

const TemperatureIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"></path>
  </svg>
)

const EfficiencyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <path d="m16 12-4-4-4 4"></path>
    <path d="M12 16V8"></path>
  </svg>
)

const InfoIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
)

// Sub-sections for thermal with icons
const THERMAL_SUB_SECTIONS = [
  { id: 'heat', label: 'Heat Loss', icon: <HeatIcon /> },
  { id: 'temperature', label: 'Temperature', icon: <TemperatureIcon /> },
  { id: 'efficiency', label: 'Efficiency', icon: <EfficiencyIcon /> }
]

// Get definition for current subsection (compare-specific, no cell-specific values)
function getDefinitionText(subSection) {
  // Map subsection ID to definition key (using compare-specific definitions)
  const definitionMap = {
    'heat': COMPARE_THERMAL_SECTION.heatPower,
    'temperature': COMPARE_THERMAL_SECTION.temperature,
    'efficiency': COMPARE_THERMAL_SECTION.efficiency
  }
  
  const definition = definitionMap[subSection]
  if (!definition) return null
  
  return {
    title: typeof definition.getTitle === 'function' 
      ? definition.getTitle(domain.value)
      : definition.title,
    text: definition.text  // No placeholder replacement needed - already generic
  }
}

export default function CompareThermalView({ view = '2d' }) {
  const [cellDataMap, setCellDataMap] = useState({})
  const [loading, setLoading] = useState(true)
  const [isFlipped, setIsFlipped] = useState(false)

  // Load FULL cell data for plots
  useEffect(() => {
    async function loadCells() {
      const cellIds = compareCells.value
      if (!cellIds || cellIds.length === 0) {
        setLoading(false)
        setCellDataMap({})
        return
      }

      setLoading(true)
      try {
        console.log('[CompareThermalView] Loading full cell data for plots:', cellIds)
        
        // Load cells in parallel with full data
        const promises = cellIds.map(async (cellId) => {
          const fullData = await getCellNormalized(cellId, { lite: false, fieldGating: 'demo' }) // Compare = browse: server-gate premium for demo
          return {
            productId: cellId,
            manufacturer: fullData?.manufacturer || 'Unknown',
            name: fullData?.name || 'Unknown',
            data: fullData
          }
        })
        
        const cellsArray = await Promise.all(promises)
        
        // Convert to map
        const dataMap = {}
        cellsArray.forEach(cell => {
          dataMap[cell.productId] = cell
        })
        
        console.log('[CompareThermalView] Loaded cells:', Object.keys(dataMap).length)
        
        setCellDataMap(dataMap)
      } catch (err) {
        console.error('[CompareThermalView] Failed to load compare cells:', err)
      } finally {
        setLoading(false)
      }
    }

    loadCells()
  }, [compareCells.value])

  const allCellDataArray = Object.values(cellDataMap).filter(Boolean)
  const cellDataArray = allCellDataArray.filter(cell => !hiddenCells.value.includes(cell.productId))

  if (loading) {
    return (
      <div className="compare-section-view loading">
        <div className="loading-spinner">Loading thermal data...</div>
      </div>
    )
  }

  if (allCellDataArray.length === 0) {
    return (
      <div className="compare-section-view empty">
        <div className="empty-state">
          <p>No cells selected for comparison</p>
          <p className="hint">Select 2-5 cells to compare thermal properties</p>
        </div>
      </div>
    )
  }
  
  if (cellDataArray.length === 0) {
    return (
      <div className="compare-section-view empty">
        <div className="empty-state">
          <p>All cells are hidden</p>
          <p className="hint">Use the eye icon in the compare panel to show cells</p>
        </div>
      </div>
    )
  }

  // 2D/3D view with sub-navigation (no table view)
  return (
    <div className="compare-section-view compare-section-view-split">
      {/* Left Sidebar: Sub-Section Navigation (Separate Card) */}
      <aside className="compare-subsection-nav compare-subsection-nav-card">
        <div className="nav-header">
          <h3>Thermal Properties</h3>
          <p className="subsection-subtitle">
            Comparing {cellDataArray.length} cells • {view === '2d' ? '2D Curves' : '3D Surfaces'}
          </p>
        </div>

        <div className="subsection-list">
          {THERMAL_SUB_SECTIONS.map(sub => (
            <button
              key={sub.id}
              className={`subsection-btn ${activeThermalSubSection.value === sub.id ? 'active' : ''}`}
              onClick={() => { activeThermalSubSection.value = sub.id; setIsFlipped(false) }}
            >
              <div className="category-icon">{sub.icon}</div>
              <div className="category-info">
                <div className="category-label">{sub.label}</div>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Main Content Area (Flipcard) */}
      <div className={`compare-subsection-content compare-subsection-flipcard flipcard ${isFlipped ? 'flipped' : ''}`}>
        <div className="flipcard-inner">
          {/* Front: Plot */}
          <div className="flipcard-front compare-flipcard-front">
            <button className="flip-btn" onClick={() => setIsFlipped(true)} title="Show definition">?</button>
            
            {/* Guide Question - Prominent header with dynamic title from definitions */}
            <h3 className="column-title">
              {getDefinitionText(activeThermalSubSection.value)?.title || (
                <>
                  {activeThermalSubSection.value === 'heat' && 'How much heat is generated during operation?'}
                  {activeThermalSubSection.value === 'temperature' && 'How does temperature vary across operating conditions?'}
                  {activeThermalSubSection.value === 'efficiency' && 'How efficient is thermal management?'}
                </>
              )}
            </h3>

            {/* Plot Area */}
            <div className="subsection-plot-area">
              <DimensionViewMultiCell
                cellDataArray={cellDataArray}
                category={activeThermalSubSection.value}
                dimension={view}
              />
            </div>

            {/* Legend (show all cells, gray out hidden ones) */}
            <div className="subsection-legend">
              <div className="legend-title">Cells:</div>
              <div className="legend-items">
                {allCellDataArray.map((cell, idx) => {
                  const isHidden = hiddenCells.value.includes(cell.productId)
                  return (
                    <div key={cell.productId} className={`legend-item ${isHidden ? 'legend-hidden' : ''}`}>
                      <span 
                        className="legend-color" 
                        style={`background: ${COMPARE_COLORS[idx % COMPARE_COLORS.length]}; opacity: ${isHidden ? 0.3 : 1}`}
                      ></span>
                      <span className="legend-label">
                        {cell.manufacturer} {cell.name}
                        {isHidden && <span className="legend-hidden-badge"> (hidden)</span>}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Back: Definition */}
          <div className="flipcard-back compare-flipcard-back">
            <button className="flip-btn flip-btn-back" onClick={() => setIsFlipped(false)} title="Close">×</button>
            <h3 className="column-title">
              {getDefinitionText(activeThermalSubSection.value)?.title || 'Definition'}
            </h3>
            <div className="flip-content">
              {(() => {
                const definition = getDefinitionText(activeThermalSubSection.value)
                if (!definition) return <p>No definition available.</p>
                return <div dangerouslySetInnerHTML={{ __html: definition.text }} />
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
